import { useCallback } from 'react';
import { purchasesAPI } from '../api';
import { useAppStore } from '../stores/useAppStore';
import { downloadPurchasePdf } from '../utils/purchasePdf';

/**
 * Downloads the purchase order PDF for a purchase that has just gone live.
 *
 * Shared by the three places a purchase can go live — creating one directly,
 * and taking a draft live from either the list or the detail page — so all
 * three produce the same document and handle failure the same way.
 */
export const usePurchasePdf = () => {
  const { addNotification } = useAppStore();

  const downloadForPurchase = useCallback(
    async (purchaseId) => {
      if (!purchaseId) return false;

      try {
        // Re-read the purchase instead of reusing the submitted form: only the
        // server response carries the item names and barcodes the PDF prints,
        // alongside the ID and timestamps it was actually saved with.
        const { data } = await purchasesAPI.get(purchaseId);
        downloadPurchasePdf(data);
        return true;
      } catch (error) {
        // The purchase is already live by this point. Reporting this as an
        // error would read as "the purchase failed" and invite a retry, which
        // for a create would add the same stock twice.
        console.error('Failed to generate purchase PDF:', error);
        addNotification({
          type: 'warning',
          title: 'PDF not downloaded',
          message: `Purchase #${purchaseId} is live, but its PDF could not be generated.`,
        });
        return false;
      }
    },
    [addNotification]
  );

  return { downloadForPurchase };
};

export default usePurchasePdf;
